
/**
 * Мапперы для клиентов между DB и Frontend
 * Согласно STYLEGUIDE.md
 */
import { DbClient, DbClientInsert, DbClientUpdate, Client, ClientCreate, ClientUpdate, ClientFormData } from '@/types/entities/client';
import type { ContactField } from '@/types/contacts';
import type { Json } from '@/types/supabase';

// Utility functions
function parseDate(dateString: string | null): Date | null {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
}

function formatDate(date: Date | null): string | null {
  if (!date) return null;
  return date.toISOString().split('T')[0];
}

// DB to Frontend mappers
export function mapDbClientToClient(dbClient: DbClient): Client {
  return {
    id: dbClient.id,
    userId: dbClient.user_id,
    createdAt: parseDate(dbClient.created_at) || new Date(),
    updatedAt: parseDate(dbClient.updated_at) || new Date(),
    rentalPointId: dbClient.rental_point_id,
    type: dbClient.type,
    legalEntityType: dbClient.legal_entity_type,
    fullName: dbClient.full_name,
    companyName: dbClient.company_name,
    inn: dbClient.inn,
    kpp: dbClient.kpp,
    phone: dbClient.phone,
    email: dbClient.email,
    address: dbClient.address,
    birthDate: parseDate(dbClient.birth_date),
    passportSeries: dbClient.passport_series,
    passportNumber: dbClient.passport_number,
    passportIssuedBy: dbClient.passport_issued_by,
    passportIssueDate: parseDate(dbClient.passport_issue_date),
    bankName: dbClient.bank_name,
    bankAccount: dbClient.bank_account,
    bankBik: dbClient.bank_bik,
    // Новые поля для юридических лиц
    bankCorrespondentAccount: dbClient.bank_correspondent_account,
    directorTitle: dbClient.director_title,
    directorName: dbClient.director_name,
    legalBasis: dbClient.legal_basis,
    contacts: dbClient.contacts as ContactField[] | null,
    comment: dbClient.comment,
    loyaltyStatus: dbClient.loyalty_status,
    status: dbClient.status,
    registrationDate: parseDate(dbClient.registration_date) || new Date(),
    remindBirthday: dbClient.remind_birthday,
    remindDaysBefore: dbClient.remind_days_before,
  };
}

export function mapDbClientsToClients(dbClients: DbClient[]): Client[] {
  return dbClients.map(mapDbClientToClient);
}

// Frontend to DB mappers
export function mapClientToDbClient(client: Client): DbClient {
  return {
    id: client.id,
    user_id: client.userId,
    created_at: client.createdAt.toISOString(),
    updated_at: client.updatedAt.toISOString(),
    rental_point_id: client.rentalPointId,
    type: client.type,
    legal_entity_type: client.legalEntityType,
    full_name: client.fullName,
    company_name: client.companyName,
    inn: client.inn,
    kpp: client.kpp,
    phone: client.phone,
    email: client.email,
    address: client.address,
    birth_date: formatDate(client.birthDate),
    passport_series: client.passportSeries,
    passport_number: client.passportNumber,
    passport_issued_by: client.passportIssuedBy,
    passport_issue_date: formatDate(client.passportIssueDate),
    bank_name: client.bankName,
    bank_account: client.bankAccount,
    bank_bik: client.bankBik,
    // Новые поля для юридических лиц
    bank_correspondent_account: client.bankCorrespondentAccount,
    director_title: client.directorTitle,
    director_name: client.directorName,
    legal_basis: client.legalBasis,
    contacts: client.contacts as Json,
    comment: client.comment,
    loyalty_status: client.loyaltyStatus,
    status: client.status,
    registration_date: client.registrationDate.toISOString(),
    remind_birthday: client.remindBirthday,
    remind_days_before: client.remindDaysBefore,
  };
}

export function mapClientCreateToDbInsert(clientCreate: ClientCreate): DbClientInsert {
  return {
    user_id: clientCreate.userId,
    rental_point_id: clientCreate.rentalPointId,
    type: clientCreate.type,
    legal_entity_type: clientCreate.legalEntityType,
    full_name: clientCreate.fullName,
    company_name: clientCreate.companyName,
    inn: clientCreate.inn,
    kpp: clientCreate.kpp,
    phone: clientCreate.phone,
    email: clientCreate.email,
    address: clientCreate.address,
    birth_date: formatDate(clientCreate.birthDate),
    passport_series: clientCreate.passportSeries,
    passport_number: clientCreate.passportNumber,
    passport_issued_by: clientCreate.passportIssuedBy,
    passport_issue_date: formatDate(clientCreate.passportIssueDate),
    bank_name: clientCreate.bankName,
    bank_account: clientCreate.bankAccount,
    bank_bik: clientCreate.bankBik,
    // Новые поля для юридических лиц
    bank_correspondent_account: clientCreate.bankCorrespondentAccount,
    director_title: clientCreate.directorTitle,
    director_name: clientCreate.directorName,
    legal_basis: clientCreate.legalBasis,
    contacts: clientCreate.contacts as Json ?? null,
    comment: clientCreate.comment,
    loyalty_status: clientCreate.loyaltyStatus,
    status: clientCreate.status,
    registration_date: clientCreate.registrationDate.toISOString(),
    remind_birthday: clientCreate.remindBirthday,
    remind_days_before: clientCreate.remindDaysBefore,
  };
}

export function mapClientUpdateToDbUpdate(clientUpdate: ClientUpdate): DbClientUpdate {
  const dbUpdate: DbClientUpdate = {};
  
  if (clientUpdate.rentalPointId !== undefined) dbUpdate.rental_point_id = clientUpdate.rentalPointId;
  if (clientUpdate.type !== undefined) dbUpdate.type = clientUpdate.type;
  if (clientUpdate.legalEntityType !== undefined) dbUpdate.legal_entity_type = clientUpdate.legalEntityType;
  if (clientUpdate.fullName !== undefined) dbUpdate.full_name = clientUpdate.fullName;
  if (clientUpdate.companyName !== undefined) dbUpdate.company_name = clientUpdate.companyName;
  if (clientUpdate.inn !== undefined) dbUpdate.inn = clientUpdate.inn;
  if (clientUpdate.kpp !== undefined) dbUpdate.kpp = clientUpdate.kpp;
  if (clientUpdate.phone !== undefined) dbUpdate.phone = clientUpdate.phone;
  if (clientUpdate.email !== undefined) dbUpdate.email = clientUpdate.email;
  if (clientUpdate.address !== undefined) dbUpdate.address = clientUpdate.address;
  if (clientUpdate.birthDate !== undefined) dbUpdate.birth_date = formatDate(clientUpdate.birthDate);
  if (clientUpdate.passportSeries !== undefined) dbUpdate.passport_series = clientUpdate.passportSeries;
  if (clientUpdate.passportNumber !== undefined) dbUpdate.passport_number = clientUpdate.passportNumber;
  if (clientUpdate.passportIssuedBy !== undefined) dbUpdate.passport_issued_by = clientUpdate.passportIssuedBy;
  if (clientUpdate.passportIssueDate !== undefined) dbUpdate.passport_issue_date = formatDate(clientUpdate.passportIssueDate);
  if (clientUpdate.bankName !== undefined) dbUpdate.bank_name = clientUpdate.bankName;
  if (clientUpdate.bankAccount !== undefined) dbUpdate.bank_account = clientUpdate.bankAccount;
  if (clientUpdate.bankBik !== undefined) dbUpdate.bank_bik = clientUpdate.bankBik;
  // Новые поля для юридических лиц
  if (clientUpdate.bankCorrespondentAccount !== undefined) dbUpdate.bank_correspondent_account = clientUpdate.bankCorrespondentAccount;
  if (clientUpdate.directorTitle !== undefined) dbUpdate.director_title = clientUpdate.directorTitle;
  if (clientUpdate.directorName !== undefined) dbUpdate.director_name = clientUpdate.directorName;
  if (clientUpdate.legalBasis !== undefined) dbUpdate.legal_basis = clientUpdate.legalBasis;
  if (clientUpdate.contacts !== undefined) dbUpdate.contacts = clientUpdate.contacts as Json;
  if (clientUpdate.comment !== undefined) dbUpdate.comment = clientUpdate.comment;
  if (clientUpdate.loyaltyStatus !== undefined) dbUpdate.loyalty_status = clientUpdate.loyaltyStatus;
  if (clientUpdate.status !== undefined) dbUpdate.status = clientUpdate.status;
  if (clientUpdate.registrationDate !== undefined) dbUpdate.registration_date = clientUpdate.registrationDate.toISOString();
  if (clientUpdate.remindBirthday !== undefined) dbUpdate.remind_birthday = clientUpdate.remindBirthday;
  if (clientUpdate.remindDaysBefore !== undefined) dbUpdate.remind_days_before = clientUpdate.remindDaysBefore;
  
  return dbUpdate;
}

// Form data mappers
export function mapClientFormDataToDbInsert(formData: ClientFormData, userId: string): DbClientInsert {
  return {
    user_id: userId,
    rental_point_id: '', // Will be set by the application
    type: formData.type,
    legal_entity_type: formData.legalEntityType as 'ip' | 'ooo' | null,
    full_name: formData.fullName || null,
    company_name: formData.companyName || null,
    inn: formData.inn || null,
    kpp: formData.kpp || null,
    phone: formData.phone || null,
    email: formData.email || null,
    address: formData.address || null,
    birth_date: formData.birthDate || null,
    passport_series: formData.passportSeries || null,
    passport_number: formData.passportNumber || null,
    passport_issued_by: formData.passportIssuedBy || null,
    passport_issue_date: formData.passportIssueDate || null,
    bank_name: formData.bankName || null,
    bank_account: formData.bankAccount || null,
    bank_bik: formData.bankBik || null,
    // Новые поля для юридических лиц
    bank_correspondent_account: formData.bankCorrespondentAccount || null,
    director_title: formData.directorTitle || null,
    director_name: formData.directorName || null,
    legal_basis: formData.legalBasis || null,
    contacts: null,
    comment: formData.comment || null,
    loyalty_status: 'white',
    status: null,
    registration_date: new Date().toISOString(),
    remind_birthday: formData.remindBirthday || false,
    remind_days_before: formData.remindDaysBefore || 3,
  };
}

export function mapClientToFormData(client: Client): ClientFormData {
  return {
    type: client.type,
    fullName: client.fullName || undefined,
    companyName: client.companyName || undefined,
    phone: client.phone || undefined,
    email: client.email || undefined,
    address: client.address || undefined,
    comment: client.comment || undefined,
    birthDate: client.birthDate ? formatDate(client.birthDate) || undefined : undefined,
    remindBirthday: client.remindBirthday || undefined,
    remindDaysBefore: client.remindDaysBefore || undefined,
    passportSeries: client.passportSeries || undefined,
    passportNumber: client.passportNumber || undefined,
    passportIssuedBy: client.passportIssuedBy || undefined,
    passportIssueDate: client.passportIssueDate ? formatDate(client.passportIssueDate) || undefined : undefined,
    inn: client.inn || undefined,
    kpp: client.kpp || undefined,
    legalEntityType: client.legalEntityType || undefined,
    bankName: client.bankName || undefined,
    bankAccount: client.bankAccount || undefined,
    bankBik: client.bankBik || undefined,
    // Новые поля для юридических лиц
    bankCorrespondentAccount: client.bankCorrespondentAccount || undefined,
    directorTitle: client.directorTitle || undefined,
    directorName: client.directorName || undefined,
    legalBasis: client.legalBasis || undefined,
  };
}
